import { useAnimationStep, type UseAnimationStep } from "@/utils/animation/useAnimationStep";
import { useElementBounding, useElementSize, useWindowSize } from "@vueuse/core";
import { lerp, smoothstep, VEC2, Vector, type AABB } from "math";
import type { ComputedRef, CSSProperties, MaybeRef } from "vue";
import { computed, ref, unref, type Ref } from "vue"

export type UsePopoverProps = {
  target?: Ref;
  self?: Ref;
  fitTargetWidth?: boolean;
  direction?: MaybeRef<Vector>;
}

export type UsePopover = {
  style: ComputedRef<CSSProperties>;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  isOpen: Ref<boolean>;
  animation: UseAnimationStep;
}

export const usePopover = (props: UsePopoverProps): UsePopover => {
  const win = useWindowSize();
  
  const isOpen = ref<boolean>(false);
  const openAnimation = useAnimationStep({
    duration: 0.25,
    initial: 0.0
  })

  const direction = computed((): Vector => (unref(props.direction) || VEC2(0, 1)).unit());
  const animatedDirection = computed((): Vector => {
    const f = openAnimation.current.value;
    const sf = smoothstep(0.0, 0.75, f);
    return direction.value.scale(lerp(0.75, 1.0, sf));
  })
  
  const setOpen = (open: boolean) => {
    openAnimation.to(Number(open));
    isOpen.value = open;
  }

  const toggleOpen = () => {
    setOpen(!isOpen.value);
  }
  
  const selfSize = props.self ? useElementSize(props.self, undefined, { box: 'border-box' }) : null;
  const targetBounds = props.target ? useElementBounding(props.target) : null;

  const targetAABB = computed(():AABB => {
    if (!targetBounds) return { min: VEC2(0, 0), max: VEC2(0, 0) };
    const min = VEC2(targetBounds.x.value, targetBounds.y.value);
    const max = min.add(VEC2(targetBounds.width.value, targetBounds.height.value));
    return { min, max };
  });

  const mySize = computed((): Vector => {
    if (!selfSize) return VEC2(0, 0);
    return VEC2(selfSize.width.value, selfSize.height.value);
  });

  const targetSize = computed((): Vector => {
    if (!targetBounds) return VEC2(0,0);
    return VEC2(targetBounds.width.value, targetBounds.height.value);
  });


  const pos = computed((): Vector => {
    const tbound = targetAABB.value;
    const tpos = tbound.min.add(tbound.max).scale(0.5).sub(mySize.value.scale(0.5));
    const dir = animatedDirection.value;
    const sz = mySize.value;

    const p = tpos.add(dir.scale(0.5 * dir.dot(targetSize.value)))
                .add(dir.scale(0.5 * dir.dot(mySize.value)));

    const overflowY = Math.max(0, (p.y + sz.y) - win.height.value);
    p.y -= overflowY;
    
    return p;
  });


  const style = computed(():CSSProperties => {
    const p = pos.value;
    const f = openAnimation.current.value;
    const sf = smoothstep(0.0, 1.0, f);
    const opacity = lerp(0.0, 100.0, sf);

    return {
      position: 'fixed',
      left: p.x + 'px',
      top: p.y + 'px',
      opacity: `${opacity}%`,
      ...(opacity <= 0.00001 ? {
        display: 'none',
        pointerEvents: 'none'
      } : {}),
      zIndex: 140,
      ...(props.fitTargetWidth && targetBounds ? {
        minWidth: targetBounds.width.value + 'px'
      } : {}),
    }
  });

  return {
    style,
    setOpen,
    toggleOpen,
    isOpen,
    animation: openAnimation
  }
}
